import type { PhoneSignInConsent } from '@/apis/experience/contextual-sign-in';
import type { AuthenticationOptionsV3 } from '@/types/contextual-auth-options.generated';

export type ContextualSignInPage = {
  revision: 1;
  options: AuthenticationOptionsV3;
  consent: Omit<PhoneSignInConsent, 'accepted'>;
  parentOrigin?: string;
};
const allowedParent = (value: unknown) =>
  value === 'https://test.vibehub.pub' || value === 'https://vibehub.pub';
const object = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const version = (value: unknown): value is string =>
  typeof value === 'string' && /^[\dA-Za-z][\w.-]{0,63}$/.test(value);
const optionsGuard = (value: unknown): value is AuthenticationOptionsV3 =>
  object(value) &&
  value.version === 3 &&
  value.default_calling_code === '86' &&
  typeof value.wechat_enabled === 'boolean' &&
  ['embedded_enabled', 'phone_otp_enabled', 'password_enabled', 'registration_enabled'].every(
    (key) => typeof value[key] === 'boolean'
  ) &&
  Array.isArray(value.sms_regions) &&
  value.sms_regions.every(
    (region: unknown) => typeof region === 'string' && /^[A-Z]{2}$/.test(region)
  );

/** Reads a native SSR projection only. Query, storage and messages cannot enable this page. */
export const getContextualSignInPage = (
  ssr: unknown = window.logtoSsr
): ContextualSignInPage | undefined => {
  if (!object(ssr) || !object(ssr.vibehubSignIn)) {
    return;
  }
  const page = ssr.vibehubSignIn;
  if (
    page.revision !== 1 ||
    !optionsGuard(page.options) ||
    !object(page.consent) ||
    !version(page.consent.termsVersion) ||
    !version(page.consent.privacyVersion) ||
    (page.parentOrigin !== undefined && !allowedParent(page.parentOrigin)) ||
    (page.options.embedded_enabled && !allowedParent(page.parentOrigin))
  ) {
    return;
  }
  return {
    revision: 1,
    options: page.options,
    consent: {
      termsVersion: page.consent.termsVersion,
      privacyVersion: page.consent.privacyVersion,
    },
    ...(page.parentOrigin === undefined ? {} : { parentOrigin: page.parentOrigin }),
  };
};

/** Layout is not authentication. Native CSP validates all ancestors independently. */
export const isContextualSignInEmbedded = (
  page: ContextualSignInPage | undefined,
  context?: { parent: unknown; self: unknown }
) =>
  Boolean(
    page?.options.embedded_enabled &&
    allowedParent(page.parentOrigin) &&
    (context ?? window).parent !== (context?.self ?? window)
  );
